/* review-workspace-sabre.jsx — real SABRE Review-tab artifacts.
   Layer 1 and qualification stay visibly separate; missing artifacts stay
   missing, and pre-SABRE jobs retain the legacy Review-tab contract. */

import { currentChecklistComplete, currentChecklistBody, verdictError, verdictContext } from '../lib/review-verdict.js';
import { sabreStopEvidence } from '../lib/sabre-stop-evidence.js';

const SQ_CHECKS = {
  no_script: ['No script tags', 'No <script> anywhere in the HTML'],
  no_remote_images: ['No remote images', 'Every image is local or bound'],
  remote_images_authorized: ['Remote images authorized', 'Hosted images must match the sealed asset authority'],
  no_whole_page_fallback: ['No whole-page raster', 'No whole/full-page raster transform'],
  responsive_contract: ['Responsive contract', '600px container and responsive regions'],
  account_header_omitted: ['Account header omitted', 'Design chrome account header is omitted'],
  success_simplified_omitted: ['Success-Simplified omitted', 'Success-Simplified band is omitted'],
  no_untrusted_reference_authority: ['No untrusted authority', 'No forbidden source digest is authoritative'],
  render_viewports: ['Renders clean · 600 + 320', 'Both required viewports render cleanly'],
  anchor_render_identity: ['Anchor render identity', 'Rendered anchors match their bound regions'],
};
const SQ_ORDER = Object.keys(SQ_CHECKS);
const SQ_GATE_LABEL = {
  blind_target_custody: 'Blind target custody',
  product_qa: 'Product QA',
  deterministic_replay: 'Deterministic replay',
  copy_recall: 'Copy recall',
  copy_precision: 'Copy precision',
  interaction_parity: 'Interaction parity',
  required_omissions: 'Required omissions',
  asset_similarity: 'Asset similarity',
  render_similarity: 'Render similarity',
  dialect_similarity: 'Dialect similarity',
  container_width: 'Container width',
  required_role_rules: 'Required role rules',
  interaction_atom_binding: 'Interaction atom binding',
};
const SQ_ARTIFACTS = {
  receipt: '05_approval/sabre-run-receipt.v1.json',
  layer1: '04_qa/qa-report.json',
  needsInput: '05_approval/needs-input.v1.json',
  /* The qualification CLI currently requires a caller-owned --output. This is
     the Review-page convention until the producer materializes a canonical rel. */
  qual: '04_qa/sabre-build-qual-report.v1.json',
};
const SQ_RECEIPT_STATES = {
  candidate_ready: {
    label: 'Candidate ready',
    className: 'pass',
    icon: 'check',
    disposition: 'Shippable candidate',
    next: 'Review full-email QA and record the developer verdict.',
  },
  needs_input: {
    label: 'Needs input',
    className: 'minor',
    icon: 'warn',
    disposition: 'Reviewable, non-shippable',
    next: 'Resolve the required input, then rerun SABRE.',
  },
  qa_exhausted: {
    label: 'QA exhausted',
    className: 'info',
    icon: 'minus',
    disposition: 'Reviewable, non-shippable',
    next: 'Amend the named failure, then rerun layer-1 QA.',
  },
};
const SQ_BLOCK_ALIASES = {
  bar: ['brand accent bar', 'brand_accent_bar', 'accent bar'],
  logo: ['brand logo lockup', 'brand_logo_lockup', 'wordmark'],
  hero: ['hero media', 'hero_media'],
  headline: ['offer headline body', 'offer_headline_body', 'headline body', 'body copy'],
  ratetable: ['rate disclosure table', 'rate_disclosure_table', 'rate table'],
  cta: ['primary cta', 'primary_cta', 'call to action'],
  footer: ['legal footer', 'legal_footer', 'disclaimer'],
};
const SQ_CHASE_GATE = 'Chase proposals land with the amendment service (ticket RWB-7) — not wired in this lane.';

const sqHuman = (value) => String(value || '').replace(/[_-]+/g, ' ').replace(/^./, (letter) => letter.toUpperCase());
const sqArray = (value) => Array.isArray(value) ? value : [];
const sqDigest = (value) => String(value || '').replace(/^sha256:/, '').slice(0, 8);

async function sqFetchJson(jobId, relative) {
  try {
    const result = await window.GooseAPI.fetchArtifactText(jobId, relative);
    if (!result || result.text == null) return null;
    const value = typeof result.text === 'string' ? JSON.parse(result.text) : result.text;
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  } catch (_) {
    return null;
  }
}

async function sqFetchSabreJson(jobId, relative, schemaVersion) {
  try {
    const result = await window.GooseAPI.fetchArtifactText(jobId, relative);
    if (!result || result.text == null) return { value: null, error: 'Artifact response had no body.' };
    const value = typeof result.text === 'string' ? JSON.parse(result.text) : result.text;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return { value: null, error: 'Artifact JSON was not an object.' };
    if (value.schema_version !== schemaVersion) {
      return { value: null, error: 'Unexpected schema ' + String(value.schema_version || 'missing') + '; expected ' + schemaVersion + '.' };
    }
    return { value, text: typeof result.text === 'string' ? result.text : null, error: null };
  } catch (error) {
    if (error && Number(error.status) === 404) return { value: null, error: null };
    if (error instanceof SyntaxError) return { value: null, error: 'Artifact JSON could not be parsed.' };
    return { value: null, error: error && error.message ? error.message : 'Artifact could not be read.' };
  }
}

async function sqFetchText(jobId, relative) {
  try {
    const result = await window.GooseAPI.fetchArtifactText(jobId, relative);
    return result && typeof result.text === 'string' ? result.text : '';
  } catch (_) {
    return '';
  }
}

function sqQualArtifact(job) {
  return job.sabreQualReportPath || job.sabre_qual_report_path || job.qualReportPath || job.qual_report_path || SQ_ARTIFACTS.qual;
}

function sqUseSabreArtifacts(job, enabled) {
  const qualPath = sqQualArtifact(job);
  const fetchable = !!enabled && window.RW_SABRE_ARTIFACTS !== false && !!(window.GooseAPI && window.GooseAPI.fetchArtifactText);
  const contextKey = verdictContext(window.MAV, job.id, qualPath);
  const key = (fetchable ? 'on:' : 'off:') + contextKey;
  const empty = { key, loading: fetchable, receipt: null, layer1: null, qual: null, stopEvidence: null, receiptError: null, layer1Error: null, qualError: null };
  const [state, setState] = React.useState(empty);
  React.useEffect(() => {
    let alive = true;
    if (!fetchable) {
      setState({ ...empty, loading: false });
      return () => { alive = false; };
    }
    setState(empty);
    Promise.all([
      sqFetchSabreJson(job.id, SQ_ARTIFACTS.receipt, 'SabreRunReceipt.v1'),
      sqFetchSabreJson(job.id, SQ_ARTIFACTS.layer1, 'sabre-qa-report/1.0'),
      sqFetchSabreJson(job.id, qualPath, 'SabreBuildQualReport.v1'),
      sqFetchSabreJson(job.id, SQ_ARTIFACTS.needsInput, 'NeedsInput.v1'),
    ]).then(async ([receipt, layer1, qual, needsInput]) => {
      const stopEvidence = await sabreStopEvidence({ jobId:job.id, receipt:receipt.value,
        layer1:layer1.value, layer1Text:layer1.text, needsInput:needsInput.value, needsInputText:needsInput.text });
      if (alive && verdictContext(window.MAV, job.id, qualPath) === contextKey) setState({
        key,
        loading: false,
        receipt: receipt.value,
        layer1: layer1.value,
        qual: qual.value,
        stopEvidence,
        receiptError: receipt.error,
        layer1Error: layer1.error,
        qualError: qual.error,
      });
    });
    return () => { alive = false; };
  }, [fetchable, job.id, qualPath, contextKey]);
  return state.key === key ? state : empty;
}

function sqUseLegacyArtifacts(job, enabled) {
  const fetchable = !!enabled && !!(window.GooseAPI && window.GooseAPI.fetchArtifactText);
  const key = (fetchable ? 'on:' : 'off:') + String(job.id || '');
  const empty = { key, loading: fetchable, report: null, markdown: '', imgRefAudit: null, textAccuracy: null };
  const [state, setState] = React.useState(empty);
  React.useEffect(() => {
    let alive = true;
    if (!fetchable) {
      setState({ ...empty, loading: false });
      return () => { alive = false; };
    }
    setState(empty);
    Promise.all([
      sqFetchJson(job.id, 'qa_report.json'),
      sqFetchText(job.id, '04_qa/qa-report.md'),
      sqFetchJson(job.id, '04_qa/image-ref-audit.json'),
      sqFetchJson(job.id, '04_qa/text-accuracy-audit.json'),
    ]).then(([report, markdown, imgRefAudit, textAccuracy]) => {
      if (alive) setState({ key, loading: false, report, markdown, imgRefAudit, textAccuracy });
    });
    return () => { alive = false; };
  }, [fetchable, job.id]);
  return state.key === key ? state : empty;
}

/* Failures are always expanded. Passing rows disclose on demand. */
function SqLedger({ rows, label }) {
  const [open, setOpen] = React.useState(false);
  const safeRows = sqArray(rows);
  const bad = safeRows.filter((row) => !row.ok);
  const good = safeRows.filter((row) => row.ok);
  return (
    <React.Fragment>
      <div className="w-sq-strip" aria-label={safeRows.length + ' ' + label}>
        {safeRows.map((row, index) => <i key={row.name + ':' + index} className={row.ok ? 'ok' : 'no'} data-sq-row={row.name} data-sq-result={row.ok ? 'pass' : 'fail'} title={row.label + (row.ok ? ' · pass' : ' · fail')}></i>)}
      </div>
      {bad.map((row, index) => (
        <div key={row.name + ':' + index} className="w-sq-item bad" data-sq-failure={row.name}>
          <span className="w-sq-ic"><BI.x size={11} /></span>
          <span className="w-sq-it">
            <b>{row.label}</b>
            <code>{row.name}</code>
            <i>{row.detail || 'No failure detail was materialized.'}</i>
          </span>
          {row.onRegion && <button className="w-cm-act" onClick={row.onRegion}>region</button>}
        </div>
      ))}
      <button className="w-sq-more" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <BI.chevron size={11} style={{ transform: open ? 'rotate(90deg)' : 'none' }} />
        {good.length} {label} passed
      </button>
      {open && (
        <div className="w-sq-passed">
          {good.map((row, index) => <span key={row.name + ':' + index} className="w-sq-p"><BI.check size={10} />{row.label}{row.detail ? <em>{row.detail}</em> : null}</span>)}
        </div>
      )}
    </React.Fragment>
  );
}

function SqLegend() {
  return (
    <div className="w-sq-legend">
      <span className="w-sq-lg ok" title="Check or gate satisfied"><i><BI.check size={10} /></i>pass</span>
      <span className="w-sq-lg no" title="Hard failure"><i><BI.x size={10} /></i>fail</span>
      <span className="w-sq-lg mi" title="Confirm intentional; never an automatic delivery failure"><i><BI.warn size={10} /></i>minor</span>
      <span className="w-sq-lg om" title="Deliberately absent; correct behavior"><i><BI.minus size={10} /></i>omitted</span>
      <span className="w-sq-lgsep"></span>
      <span className="w-sq-lg src" title="Deterministic checks on every SABRE build; count comes from the report">sabre/1.0</span>
      <span className="w-sq-lg src" title="Hard gates on qualification runs only; count comes from the report">qual</span>
      <span className="w-sq-lg src" title="The build's own per-region findings">build qa</span>
    </div>
  );
}

function SqReceiptStrip({ receipt, loading, error, compact = false, qualified = false }) {
  if (loading) {
    return <div className="w-callout info" data-review-receipt="loading"><BI.clock size={15} /><span>Checking SABRE run receipt…</span></div>;
  }
  if (error) {
    return <div className="w-callout minor" data-review-receipt="error"><BI.warn size={15} /><span><b>Run receipt could not be read</b> · {error}</span></div>;
  }
  if (!receipt) {
    return <div className="w-callout info" data-review-receipt="absent"><BI.minus size={15} /><span><b>Run receipt</b> · Not materialized for this lane</span></div>;
  }
  const state = SQ_RECEIPT_STATES[receipt.status] || {
    label: sqHuman(receipt.status || 'Run receipt'),
    className: 'info',
    icon: 'minus',
    disposition: 'Review state recorded',
    next: 'Inspect the run receipt before making a delivery decision.',
  };
  const ReceiptIcon = BI[state.icon] || BI.minus;
  const repairs = receipt.repair_count;
  const next = receipt.next || state.next;
  if (compact) return <div className="w-sq-head" data-review-receipt={receipt.status || 'unknown'} data-review-shippable={receipt.status === 'candidate_ready' ? 'true' : 'false'}>
    <span className={'w-abp ' + (receipt.status === 'candidate_ready' ? 'pass' : 'cond')}>{receipt.status === 'candidate_ready' ? <i /> : <BI.warn size={10}/>} {state.label}</span>
    {qualified && <span className="w-variant" style={{ background: '#5B4B8A' }}>QUAL RUN</span>}
    <span className="w-sq-src" title={'sabre-qa-report/1.0 · run receipt ' + receipt.status}>sabre/1.0{repairs != null ? ' · ' + repairs + ' repair' + (repairs === 1 ? '' : 's') : ''}</span>
  </div>;
  return (
    <div className={'w-callout ' + state.className} data-review-receipt={receipt.status || 'unknown'} data-review-shippable={receipt.status === 'candidate_ready' ? 'true' : 'false'}>
      <ReceiptIcon size={15} />
      <span>
        <b>{state.label}</b> · {state.disposition}{repairs != null ? ' · ' + repairs + ' repair' + (repairs === 1 ? '' : 's') : ''}
        <br /><span>Next: {next}</span>
      </span>
    </div>
  );
}

function SqStopCard({ receipt, evidence }) {
  if (!['needs_input', 'qa_exhausted'].includes(receipt?.status)) return null;
  const bound = evidence?.state === 'bound';
  const state = SQ_RECEIPT_STATES[receipt.status];
  return <section className="w-sq-stop" data-sabre-stop={receipt.status} role="status">
    <div className="w-sq-stop-h"><BI.warn size={15}/><b>{state.label} · non-shippable</b><span className="w-grow"/><code>{receipt.status}</code></div>
    <p>{bound ? evidence.message : evidence?.reason || 'Detailed blocking evidence is unavailable. Reload the build artifacts.'}</p>
    <dl className="w-sq-stop-d">
      <dt>Blocked</dt><dd>{bound ? evidence.blocked : receipt.status}</dd>
      <dt>To clear it</dt><dd>{bound ? evidence.correction : 'Reload or repair the missing/broken evidence before acting on its details.'}</dd>
      <dt>Who</dt><dd>{bound ? evidence.who : 'Not specified by verified evidence'}</dd>
      <dt>Then</dt><dd>{bound ? evidence.then : 'Rerun with the correction; this stopped run cannot authorize delivery.'}</dd>
      {bound && !!evidence.missingRoles?.length && <React.Fragment><dt>Missing</dt><dd>{evidence.missingRoles.join(', ')}</dd></React.Fragment>}
      {bound && !!evidence.candidateRefs?.length && <React.Fragment><dt>Candidates</dt><dd>{evidence.candidateRefs.join(', ')}</dd></React.Fragment>}
    </dl>
    <span className="w-sq-stop-f"><b className="w-sq-n" title="receipt.repair_count">{receipt.repair_count ?? 'Unknown'}</b> automatic repairs recorded · original run state, not current-revision QA</span>
  </section>;
}

function sqLayerRows(layer1, B) {
  const checks = sqArray(layer1 && layer1.checks);
  return checks.slice().sort((left, right) => {
    const li = SQ_ORDER.indexOf(left.name);
    const ri = SQ_ORDER.indexOf(right.name);
    return (li < 0 ? SQ_ORDER.length : li) - (ri < 0 ? SQ_ORDER.length : ri);
  }).map((check) => {
    const definition = SQ_CHECKS[check.name] || [sqHuman(check.name), 'Deterministic SABRE check'];
    const block = sqFindBlock(B, check.region, check.detail);
    return {
      name: check.name,
      ok: check.ok === true,
      detail: check.detail,
      label: definition[0],
      onRegion: block && B && typeof B.setSel === 'function' ? () => B.setSel(block.id) : null,
    };
  });
}

function SqLayerOneCard({ layer1, loading, error, B, jobId }) {
  const rows = sqLayerRows(layer1, B);
  const renderPaths = Array.isArray(layer1?.renders) ? [...new Set(layer1.renders)].filter(path => ['render-600.png','render-320.png'].includes(path))
    : layer1?.checks?.some(row => row.name === 'render_viewports' && row.ok === true) ? ['render-600.png','render-320.png'] : [];
  const passed = rows.filter((row) => row.ok).length;
  const digests = layer1 ? [
    ['contract', layer1.contract_digest],
    ['asset manifest', layer1.asset_manifest_digest],
    ['html', layer1.html_digest],
  ].filter((entry) => entry[1]) : [];
  return (
    <div className="w-sq-card" style={{ flexShrink: 0 }} data-qa-card="SABRE layer 1" data-sabre-layer="1">
      <div className="w-sq-ch">
        <b>Product QA</b>
        <span className="w-sq-layer" title="Original build evidence, bound to its HTML digest; not a measurement of later edits">layer 1 · every build</span>
        <span className="w-grow" style={{ flex: 1 }}></span>
        {layer1 && <span className="w-sq-result"><span className={'w-abp ' + (layer1.status === 'PASS' ? 'pass' : 'fail')}><i></i>{layer1.status || 'UNKNOWN'}</span><span className="w-meta">{passed}/{rows.length}</span></span>}
        {!!(B && B.touched && B.touched.size) && <span className="chip">Draft changes are not covered by these measurements</span>}
      </div>
      {loading
        ? <div className="w-find-none">Checking the last certified layer-1 artifact…</div>
        : error
          ? <div className="w-callout minor"><BI.warn size={15} /><span><b>Layer-1 artifact could not be read</b> · {error}</span></div>
          : layer1
          ? <React.Fragment>
              <SqLedger rows={rows} label="checks" />
              {(!!digests.length || jobId && !!renderPaths.length && window.GooseAPI?.artifactUrl) && <div className="w-sq-digests">
                {digests.map(([label, digest]) => <span key={label} className="w-sq-dg" title={label + ' digest: ' + digest}><i>{label}</i><code>{sqDigest(digest)}</code></span>)}
                {jobId && window.GooseAPI?.artifactUrl && renderPaths.map(path => <a key={path} className="w-sq-dg art" href={window.GooseAPI.artifactUrl(jobId, '04_qa/' + path)} target="_blank" rel="noreferrer" title={'Original build render · ' + path}><BI.image size={10}/>{path.match(/\d+/)[0]}</a>)}
              </div>}
            </React.Fragment>
          : <div className="w-find-none">Not materialized for this lane</div>}
    </div>
  );
}

function sqMetricRows(metrics) {
  if (!metrics || typeof metrics !== 'object') return [];
  return Object.entries(metrics).map(([axis, value]) => {
    const rows = [];
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const prefix = entry.viewport != null ? entry.viewport + 'px ' : '';
        Object.entries(entry).forEach(([name, datum]) => {
          if (name !== 'viewport' && !/digest/i.test(name) && (typeof datum === 'string' || typeof datum === 'number' || typeof datum === 'boolean')) rows.push([prefix + sqHuman(name), String(datum)]);
        });
      });
    } else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([name, datum]) => {
        if (!/digest|tokens|assignments|absent_terms|expected_links/i.test(name) && (typeof datum === 'string' || typeof datum === 'number' || typeof datum === 'boolean')) rows.push([sqHuman(name), String(datum)]);
      });
    }
    return { axis, rows };
  }).filter((entry) => entry.rows.length);
}

function sqUseQualificationReview(job,report,enabled) {
  const api=window.GooseAPI,context=JSON.stringify([window.MAV?.__contextKey,window.MAV?.__contextEpoch,window.MAV?.currentUser?.id,window.MAV?.activeWorkspaceId]);
  const identity=context+':'+job.id+':'+(report?.report_digest||'');
  const [state,setState]=React.useState({identity:null,data:null,busy:false,error:null,uncertain:false});
  const serial=React.useRef(0),latest=React.useRef(identity);latest.current=identity;
  const accept=value=>{
    if(value?.schema!=='QualificationReview.v1'||value.job_id!==job.id||value.workspace_id!==window.MAV?.activeWorkspaceId
      ||value.report?.report_digest!==report.report_digest||value.promotion_performed!==false||value.revision_approval_changed!==false
      ||!Array.isArray(value.events)||!Array.isArray(value.correction_codes))throw Error('qualification_response_unbound');
    return value;
  };
  const request=async(body=null)=>{
    const epoch=++serial.current,scope=identity;
    setState(s=>({...s,identity,busy:true,error:null}));
    try{
      if(!api?.qualificationReview)throw Error('qualification_review_unavailable');
      const key=body?Array.from(crypto.getRandomValues(new Uint8Array(24)),v=>v.toString(16).padStart(2,'0')).join(''):null;
      const value=accept(await api.qualificationReview(job.id,body,key));
      if(serial.current===epoch&&latest.current===scope)setState({identity,data:value,busy:false,error:null,uncertain:false});
    }catch(error){if(serial.current===epoch&&latest.current===scope)setState(s=>({...s,identity,busy:false,error:error?.data?.code||error.message||'qualification_review_unavailable',uncertain:!!body&&!error.status}));}
  };
  React.useEffect(()=>{
    setState({identity,data:null,busy:false,error:null,uncertain:false});
    if(enabled&&report?.report_digest)request();
    return()=>{serial.current++;};
  },[identity,enabled]);
  return {...(state.identity===identity?state:{data:null,busy:true,error:null,uncertain:false}),reload:()=>request(),submit:request};
}

function SqQualificationVerdict({report,review}) {
  const [choices,setChoices]=React.useState({}),[note,setNote]=React.useState('');
  const data=review.data,active=data?.active,events=data?.events||[],regions=sqArray(report.decisions);
  const currentReport=report.schema_version==='CurrentRevisionQaReviewReport.v1';
  React.useEffect(()=>{setChoices({});setNote('');},[report.report_digest,data?.last_event_digest]);
  const update=(id,part)=>setChoices(s=>({...s,[id]:{...s[id],...part}}));
  const complete=currentChecklistComplete(regions,choices);
  const corrections=regions.some(r=>choices[r.region_id]?.action==='correct');
  const disabled=review.busy||review.uncertain||!!review.error||review.blocked||!data?.can_review||(currentReport&&review.state!=='ready');
  const canWithdraw=!!active&&active.reviewer.principal_id===window.MAV?.currentUser?.id
    &&(currentReport?data.can_withdraw===true:data?.can_review);
  const withdrawDisabled=review.busy||review.uncertain||!!review.error||!canWithdraw
    ||(currentReport&&!['ready','historical'].includes(review.state));
  const submit=action=>review.submit(currentChecklistBody(report,data,choices,action,note));
  return <div className="w-sq-verdict" data-qualification-review={currentReport?'current':'original'}>
    <div className="w-sq-vh"><BI.review size={14}/><b>{active?'Saved developer verdict':'Developer verdict'}</b><span className="w-grow"/><span className="w-espv noop" title="A qual result is never itself an approval">promotion_eligible: false</span></div>
    <p className="w-bar-note">{currentReport?'Bound to this exact saved revision and QA evidence. Recording the checklist does not approve delivery or any ESP output.':'Applies only to this original qualification report. It does not approve an edited email or an ESP output.'}</p>
    {review.blocked&&<p className="w-bar-note">Resolve the local draft or reload the current editable revision before recording a new verdict. Withdrawing a saved verdict does not change your HTML draft.</p>}
    {review.error&&<div className="w-callout fail" role="alert" data-qualification-error={review.error}><span>{verdictError(review.error)}. {review.uncertain?'The response was uncertain; recover the same submission before any new action.':'Reload to recover or ask an independent reviewer.'}</span></div>}
    <button type="button" className="w-cm-act" disabled={review.busy} onClick={review.reload}>Reload saved verdicts</button>
    {review.uncertain&&review.recover&&<button type="button" className="w-cm-act" disabled={review.busy||(review.blocked&&review.recoveryAction!=='withdraw')} onClick={review.recover}>Recover last submission</button>}
    {review.busy&&<p role="status">Saving or loading review…</p>}
    {data&&!data.can_review&&<p className="w-bar-note">{currentReport&&data.current===false?'Historical evidence cannot receive a new positive or corrective verdict. The authorized original reviewer may withdraw the retained judgment.':data.report_verification==='producer_unverified'?'Producer-unverified report. A trusted scorer must rederive and attest this report before a qualification verdict can be recorded. The figures are historical evidence, not certification.':'An independent reviewer with review permission must record this verdict.'}</p>}
    {active?<div className="w-sq-vdone"><span className={'w-abp '+(active.action==='approve'?'pass':'cond')}><i/>{active.action==='approve'?(currentReport?'Checklist accepted':'Approved original qualification'):'Amend requested'}</span>
      <span className="w-sq-vnote">{active.reviewer.principal_id} · {active.decided_at}</span><code>{sqDigest(active.verdict_digest)}</code>
    </div>:null}
    {data?.can_review&&<>
      <div className="w-qualification-regions">{regions.map(r=>{const c=choices[r.region_id]||{};return <details key={r.region_id} className="w-qualification-region" data-qualification-region={r.region_id}>
        <summary><b>{sqHuman(r.semantic_role||r.region_id)}</b><span>{c.action==='accept'?'Accepted':c.action==='correct'?'Correction':'Not reviewed'}</span></summary>
        <p className="w-bar-note"><code>{r.region_id}</code> · {r.disposition} · {r.renderer_recipe} · {r.authority_selected}</p>
        {currentReport&&<p className="w-bar-note" data-measurement-scope={r.measurement_scope}>{r.measurement_scope==='full_email'?'Review the whole-email measurements, named warnings and measurement gaps.':'Review this contract decision, including omissions. Whole-email QA is not an individual block pass.'}</p>}
        <label className="w-lbl">Section decision<select className="w-in" aria-label={'Decision for '+r.region_id} disabled={disabled} value={c.action||''} onChange={e=>update(r.region_id,{action:e.target.value})}><option value="">Not reviewed</option><option value="accept">Accept</option><option value="correct">Correct</option></select></label>
        {c.action==='correct'&&<><label className="w-lbl">Correction type<select className="w-in" aria-label={'Correction for '+r.region_id} disabled={disabled} value={c.code||''} onChange={e=>update(r.region_id,{code:e.target.value})}><option value="">Choose a correction</option>{data.correction_codes.map(code=><option value={code} key={code}>{sqHuman(code)}</option>)}</select></label>
          <textarea className="w-in" maxLength={2000} aria-label={'Correction note for '+r.region_id} placeholder="What needs to change in this section?" value={c.note||''} disabled={disabled} onChange={e=>update(r.region_id,{note:e.target.value})}/></>}
        {c.action==='accept'&&r.acknowledgment_required&&<label className="w-lbl">Required acknowledgment<textarea className="w-in" maxLength={2000} aria-label={'Acknowledgment for '+r.region_id} placeholder="Explain your acceptance of the named warnings, unavailable measurements and stale checks." value={c.note||''} disabled={disabled} onChange={e=>update(r.region_id,{note:e.target.value})}/></label>}
      </details>;})}</div>
    </>}
    {(data?.can_review||canWithdraw)&&<>
      <textarea className="w-in" rows={2} maxLength={2000} value={note} disabled={disabled&&withdrawDisabled} onChange={e=>setNote(e.target.value)} placeholder="Review note · required to withdraw" aria-label="Qualification review note"/>
      <div className="w-sq-vacts"><button type="button" className="btn btn-primary btn-sm" disabled={disabled||!complete||corrections||(currentReport?report.status==='FAIL'||report.blocking_findings.length>0:report.status!=='PASS')} onClick={()=>submit('approve')}><BI.check size={12}/>{currentReport?'Record developer verdict':'Approve qualification'}</button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={disabled||!complete||!corrections} onClick={()=>submit('reject')}><BI.retry size={12}/>Request amendments</button>
        {canWithdraw&&<button type="button" className="w-cm-act" disabled={withdrawDisabled||!note.trim()} onClick={()=>submit('withdraw')}>Withdraw</button>}
      </div>
    </>}
    {!!events.length&&<details className="w-history-details"><summary>Saved verdict history · {events.length}</summary>{currentReport&&review.history&&<button className="w-cm-act" disabled={review.busy} onClick={()=>review.history(report.report_digest)}>Open retained report · historical</button>}{events.map(e=><div key={e.event_digest}><b>{sqHuman(e.action)}</b> · {e.actor_id} · {e.at}{e.note&&<p>{e.note}</p>}<code>{sqDigest(e.event_digest)}</code></div>)}</details>}
  </div>;
}

function SqCurrentRevisionReview({review, verdictOnly = false, embedded = false}) {
  const report=review?.data?.report;
  return <section className={embedded ? undefined : 'w-sq-card'} data-current-qa-review={review?.state||'loading'} style={embedded ? undefined : {flexShrink:0,marginBottom:14}}>
    {!embedded && <div className="w-sq-ch"><b>{verdictOnly ? 'Developer review' : 'Current-revision QA checklist'}</b><span className="w-sq-layer">whole email · not original Sabre scoring</span></div>}
    {!report&&<div className="w-pad">
      {review?.error?<p role="alert" data-qualification-error={review.error}>{verdictError(review.error)} <code>{review.error}</code></p>:<p role="status">Loading the current revision and its QA checklist…</p>}
      <button className="w-cm-act" disabled={review?.busy} onClick={review?.reload}>Reload QA checklist</button>
    </div>}
    {report&&<>
      {embedded ? <div className="w-sq-digests"><span className="w-sq-dg" title={'Saved checklist · ' + report.revision + ' · ' + report.status + ' · ' + report.report_digest}><i>checklist</i><code>{sqDigest(report.report_digest)}</code></span></div> : <p className="w-bar-note"><b>{report.revision} · {report.status}</b> · <code title={report.report_digest}>{sqDigest(report.report_digest)}</code></p>}
      {(review.state==='historical'||review.data.current!==true)&&<p className="w-callout minor" data-verdict-historical>Historical evidence — it cannot authorize this revision. Reload the current checklist.</p>}
      {!verdictOnly && <>
      <p className="w-bar-note">All measurements below cover the whole email, not individual blocks. Missing optional comparisons are not measured passes and do not recreate original Sabre qualification.</p>
      <dl className="w-sq-digests">{Object.entries(report.measurement.axes).map(([axis,value])=><div key={axis}><dt>{sqHuman(axis)}</dt><dd data-current-qa-axis={axis}>{value===true?'PASS':value===false?(report.measurement.warnings.includes(axis)?'WARNING':'FAIL'):String(value)}</dd></div>)}</dl>
      {!!report.blocking_findings.length&&<p className="w-callout fail" data-current-qa-blocking>Blocking findings: {report.blocking_findings.join(', ')}. Request corrections and rerun QA; acknowledgment cannot waive these findings.</p>}
      {!!report.measurement.warnings.length&&<p className="w-callout minor" data-current-qa-warnings>Named warnings: {report.measurement.warnings.join(', ')}</p>}
      {report.measurement.unsubscribe_policy?.enforcement==='warning'&&report.measurement.axes.unsub===false&&<p className="w-bar-note">No unsubscribe link in this file. The applicable developer references also omit it; no ESP footer ownership is implied.</p>}
      {!!report.measurement.unmeasured.length&&<p className="w-bar-note">Unmeasured axes: {report.measurement.unmeasured.join(', ')}</p>}
      {!!report.measurement.comparison_unmeasured.length&&<p className="w-bar-note">Comparison unavailable: {report.measurement.comparison_unmeasured.join(', ')}</p>}
      {!!report.measurement.stale_expensive.length&&<p className="w-bar-note">Not refreshed: {report.measurement.stale_expensive.join(', ')}</p>}
      </>}
      {verdictOnly && !!report.blocking_findings.length && <p className="w-callout fail" data-current-qa-blocking>Blocking findings: {report.blocking_findings.join(', ')}. Request corrections and rerun QA; acknowledgment cannot waive these findings.</p>}
      <SqQualificationVerdict key={report.report_digest} report={report} review={review}/>
    </>}
  </section>;
}

function SqQualificationCard({ report, B, verdict, onVerdict, review }) {
  const [note, setNote] = React.useState('');
  const [showMetrics, setShowMetrics] = React.useState(false);
  const gates = sqArray(report.hard_gates).map((gate) => {
    const block = sqFindBlock(B, gate.region, gate.detail);
    return {
      name: gate.name,
      ok: gate.passed === true,
      detail: gate.detail,
      label: SQ_GATE_LABEL[gate.name] || sqHuman(gate.name),
      onRegion: block && B && typeof B.setSel === 'function' ? () => B.setSel(block.id) : null,
    };
  });
  const needsVerdict = report.developer_verdict_required === true;
  const awaiting = report.status === 'PASS' && needsVerdict && !verdict;
  const metricRows = sqMetricRows(report.metrics);
  const caseLabel = typeof report.case_label === 'string' && /^(?!\s*$)[^\x00-\x1f\x7f]{1,120}$/.test(report.case_label) ? report.case_label : null;
  const identity = report.case_digest || report.report_id;
  return (
    <div className="w-sq-card" style={{ flexShrink: 0 }} data-qa-card="SABRE qualification" data-sabre-qual="1">
      <div className="w-sq-ch">
        <b>SABRE-QUAL</b>
        <span className="w-sq-layer" title={identity || undefined}>layer 2 · sealed case{identity ? ' ' + (caseLabel || sqDigest(identity)) : ''}</span>
        <span className="w-grow" style={{ flex: 1 }}></span>
        <span className="w-sq-result">
        <span className={'w-abp ' + (awaiting ? 'cond' : report.status === 'PASS' ? 'pass' : 'fail')} data-sabre-qual-status={awaiting ? 'awaiting_verdict' : String(report.status || 'unknown').toLowerCase()}>
          {awaiting ? <BI.review size={10} /> : <i></i>}{awaiting ? 'awaiting developer verdict' : report.status || 'UNKNOWN'}
        </span>
        <span className="w-meta" title="Hard gates passed">{gates.filter(row => row.ok).length}/{gates.length}</span>
        </span>
      </div>
      <SqLedger rows={gates} label="gates" />

      {!!metricRows.length && (
        <React.Fragment>
          <div className="w-sq-foot2">
            <button className="w-sq-more" onClick={() => setShowMetrics((value) => !value)} aria-expanded={showMetrics}>
              <BI.chevron size={11} style={{ transform: showMetrics ? 'rotate(90deg)' : 'none' }} />Diagnostic axes
            </button>
            <span className="w-meta" title="milli (0–1000) is the unit on independent axes and is never blended into a total">no aggregate score</span>
          </div>
          {showMetrics && <div className="w-sq-axes">{metricRows.map(({ axis, rows }) => <div key={axis} className="w-sq-axis"><b>{axis}</b><span>{rows.map(([name, value]) => <em key={name}>{name} <code>{value}</code></em>)}</span></div>)}</div>}
        </React.Fragment>
      )}

      {needsVerdict && review ? <SqQualificationVerdict report={report} review={review}/> : needsVerdict && (
        <div className="w-sq-verdict">
          <div className="w-sq-vh">
            <BI.review size={14} />
            <b>{verdict ? 'Developer verdict recorded' : 'awaiting developer verdict'}</b>
            <span className="w-grow" style={{ flex: 1 }}></span>
            <span className="w-espv noop" title="A qual result is never itself an approval">promotion_eligible: false</span>
          </div>
          {verdict
            ? <div className="w-sq-vdone">
                <span className={'w-abp ' + (verdict.kind === 'approve' ? 'pass' : 'cond')}><i></i>{verdict.kind === 'approve' ? 'Approved' : 'Amend'}</span>
                {verdict.note && <span className="w-sq-vnote">“{verdict.note}”</span>}
                <span className="w-grow" style={{ flex: 1 }}></span>
                <button className="w-cm-act" onClick={() => onVerdict(null)}>Withdraw</button>
              </div>
            : <React.Fragment>
                <textarea className="w-in" rows="2" value={note} onChange={(event) => setNote(event.target.value)} placeholder="What the verdict rests on — required for amend" />
                <div className="w-sq-vacts">
                  <button className="btn btn-primary btn-sm" onClick={() => onVerdict({ kind: 'approve', note: note.trim() })}><BI.check size={12} /> Approve qualification</button>
                  <button className="btn btn-ghost btn-sm" disabled={!note.trim()} title={note.trim() ? 'Record an amend verdict' : 'An amend verdict needs a reason'} onClick={() => onVerdict({ kind: 'amend', note: note.trim() })}><BI.retry size={12} /> Amend</button>
                </div>
              </React.Fragment>}
        </div>
      )}
    </div>
  );
}

function sqNormalized(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function sqBlockAliases(block) {
  return [block.id, block.label, ...(SQ_BLOCK_ALIASES[block.id] || [])].map(sqNormalized).filter((value) => value.length >= 3);
}

function sqFindBlocks(B, direct, detail) {
  const blocks = sqArray(B && B.blocks);
  const directName = sqNormalized(direct);
  if (directName) {
    const exact = blocks.find((block) => sqBlockAliases(block).includes(directName));
    if (exact) return [exact];
  }
  const haystack = ' ' + sqNormalized(detail) + ' ';
  if (haystack.trim()) {
    return blocks.filter((block) => sqBlockAliases(block).some((alias) => haystack.includes(' ' + alias + ' ')));
  }
  return [];
}

function sqFindBlock(B, direct, detail) {
  return sqFindBlocks(B, direct, detail)[0] || null;
}

function SqPreSendAndNotes({ isLive, afterburnerStatus, pre, setPre, notesOpen, notes, setNotes }) {
  const actions = isLive ? [] : sqArray(pre);
  const done = actions.filter((action) => action.done).length;
  return (
    <div className="w-sq-card" style={{ flexShrink: 0 }}>
      <div className="w-sq-ch"><b>Handoff notes</b><span className="w-sq-layer">{isLive ? 'not an approval gate' : 'demo data'}</span><span className="w-grow" style={{ flex: 1 }}></span>{!!actions.length && <span className="w-meta">{done}/{actions.length}</span>}</div>
      {isLive && <p className="w-bar-note">No pre-send checklist is recorded for this build.</p>}
      {actions.map((action, index) => (
        <button type="button" key={action.id || index} className="w-check" aria-pressed={!!action.done} disabled={typeof setPre !== 'function'} onClick={() => setPre((items) => items.map((item, itemIndex) => (item.id || itemIndex) === (action.id || index) ? { ...item, done: !item.done } : item))}>
          <span className={'w-box' + (action.done ? ' on' : '')}>{action.done && <BI.check size={12} />}</span>
          <span className={action.done ? 'done' : ''}>{action.label}</span>
        </button>
      ))}
      {notesOpen && (
        <div style={{ marginTop: 16 }}>
          <label className="w-lbl" htmlFor="review-change-notes">Change request notes</label>
          <textarea id="review-change-notes" className="w-in" rows={3} placeholder="Describe the changes needed…" value={notes || ''} onChange={(event) => typeof setNotes === 'function' && setNotes(event.target.value)}></textarea>
        </div>
      )}
      <SqAfterburnerLane isLive={isLive} current={afterburnerStatus} />
    </div>
  );
}

function sqRegionGlyph(row, report, layer1, B, current) {
  if (row.disposition === 'omit') return 'minus';
  const failed = [...sqArray(layer1?.checks).filter(item => item.ok === false), ...sqArray(report?.hard_gates).filter(item => item.passed === false)];
  const flagged = sqArray(row.findings).length > 0 || failed.some(item => (item.region_id || item.region) === row.region_id)
    || (current && sqArray(B?.blocks).some(item => item.id === row.region_id && ['minor', 'major'].includes(item.qa)));
  return flagged ? 'warn' : 'check';
}

function SqRegions({ report, layer1, B, current = false }) {
  const [showOmitted, setShowOmitted] = React.useState(false);
  const decisions = sqArray(report?.decisions).filter(row => ['ship', 'omit', 'metadata', 'inject'].includes(row.disposition));
  const omitted = decisions.filter(row => row.disposition === 'omit').length;
  return <section className="w-sq-card" data-sabre-regions="1">
    <div className="w-sq-ch"><b>Regions</b><span className="w-sq-layer" title={(current ? 'Saved-revision' : 'Original qualification') + ' decisions; metadata and ESP-owned regions are not editable blocks'}>contract · region_directives</span><span className="w-grow"/>
      {!!omitted && <button type="button" className="w-sq-more inline" aria-expanded={showOmitted} onClick={() => setShowOmitted(!showOmitted)}>{showOmitted ? 'hide' : 'show'} {omitted} omitted</button>}
    </div>
    {!decisions.length && <div className="w-find-none">No validated region decisions are available.</div>}
    {decisions.filter(row => showOmitted || row.disposition !== 'omit').map(row => {
      const block = row.disposition === 'ship' && sqArray(B?.blocks).find(item => item.id === row.region_id);
      const omit = row.disposition === 'omit';
      const glyph = sqRegionGlyph(row, report, layer1, B, current), Icon = BI[glyph];
      return <div className={'w-sq-reg' + (omit ? ' omit' : '') + (glyph === 'warn' ? ' bad' : '')} key={row.region_id} data-sabre-disposition={row.disposition}>
        <span className="w-sq-rd" data-region-glyph={glyph}><Icon size={11}/></span>
        <span className="w-sq-rb"><b>{row.semantic_role || row.region_id}</b><i>{omit ? 'disposition=omit · correct behavior, not a skip' : [row.disposition === 'inject' ? 'ESP owned' : row.disposition === 'metadata' ? 'metadata' : null, row.implementation_mode || 'mode not recorded', row.renderer_recipe || 'recipe not recorded'].filter(Boolean).join(' · ')}</i></span>
        {current && block && typeof B?.setSel === 'function' && <button type="button" className="w-cm-act" onClick={() => B.setSel(block.id)}>open</button>}
      </div>;
    })}
  </section>;
}

function SqFindings({ B, chase, unplaced, qual }) {
  const blocks = sqArray(B && B.blocks);
  const [sort, setSort] = React.useState('verdict'), [minorOnly, setMinorOnly] = React.useState(false);
  const minorCount = blocks.filter(block => block.qa === 'minor').length;
  const onlyMinor = minorOnly && minorCount > 0;
  const sorts = [['verdict', 'Verdict', 'Failures and minor findings first, then passes'], ['document', 'Block order', 'Top-to-bottom, as the email renders'], ['edited', 'Edited', 'Blocks you changed first']];
  const loose = sqArray(unplaced && unplaced.length != null ? unplaced : B && B.unplaced);
  const qualification = [
    ...sqArray(qual && qual.findings).map((finding) => ({ label: 'Hard-gate finding', detail: finding })),
    ...sqArray(qual && qual.decisions).flatMap((decision) => sqArray(decision && decision.findings).map((finding) => ({
      label: decision.semantic_role || decision.region_id || 'Region finding',
      detail: finding,
    }))),
  ];
  const touched = B && B.touched && typeof B.touched.has === 'function' ? B.touched : new Set();
  const rank = block => ({major:0,minor:1,pass:3}[block.qa] ?? 2);
  const visible = blocks.filter(block => !onlyMinor || block.qa === 'minor').map((block,index) => ({block,index})).sort((a,b) =>
    (sort === 'verdict' ? rank(a.block)-rank(b.block) : sort === 'edited' ? Number(touched.has(b.block.id))-Number(touched.has(a.block.id)) : 0) || a.index-b.index).map(row => row.block);
  return (
    <div className="w-sq-card" style={{ flexShrink: 0 }} data-sabre-findings="1">
      <div className="w-sq-ch"><b>Block findings</b><span className="w-sq-layer">build qa · per-block notes</span><span className="w-grow" style={{ flex: 1 }}/>
        <span className="w-meta">{blocks.filter(b => b.qa === 'pass').length} pass · {minorCount} minor{blocks.some(b => b.qa === 'major') ? ' · ' + blocks.filter(b => b.qa === 'major').length + ' fail' : ''}{blocks.some(b => !b.qa) ? ' · ' + blocks.filter(b => !b.qa).length + ' ungraded' : ''}</span>
      </div>
      <div className="w-find-bar"><span className="w-find-sl">Sort</span><div className="w-seg sm" role="group" aria-label="Sort findings">
        {sorts.map(([key,label,title]) => <button type="button" key={key} title={title} className={sort===key?'on':''} aria-pressed={sort===key} onClick={() => setSort(key)}>{label}</button>)}</div>
        <span className="w-grow" style={{ flex: 1 }}/>
        <button type="button" className={'w-find-only'+(onlyMinor?' on':'')} aria-pressed={onlyMinor} disabled={!minorCount} title={!minorCount ? 'No minor findings in this revision' : onlyMinor ? 'Show every block' : 'Show only the ' + minorCount + ' minor findings'} onClick={() => setMinorOnly(!minorOnly)}>{onlyMinor ? <BI.check size={11}/> : <span className="w-find-box"/>}Minor only</button></div>
      {visible.map((block) => {
        const passed = block.qa === 'pass';
        const flagged = block.qa === 'minor' || block.qa === 'major';
        return (
          <div key={block.id} className={'w-sq-bf' + (passed ? ' ok' : !flagged ? ' ungraded' : '')} data-review-finding={block.id}>
            <span className={'w-sq-rd' + (flagged ? ' bad' : '')} style={!block.qa ? { background: 'var(--sink)', color: 'var(--muted)' } : null}>{passed ? <BI.check size={11} /> : flagged ? <BI.warn size={11} /> : <BI.minus size={11} />}</span>
            <span className="w-sq-it"><b>{block.label}</b><code>{block.id}</code><i>{block.note || 'No finding names this region.'}</i></span>
            {touched.has(block.id) && <span className="w-espv noop" title="Edited this session — QA re-runs on apply">edited</span>}
            {B && typeof B.setSel === 'function' && <button className="w-cm-act" onClick={() => B.setSel(block.id)}>open</button>}
          </div>
        );
      })}
      {!blocks.length && <div className="w-find-none">No editable regions materialized for this lane.</div>}
      {!!blocks.length && !visible.length && <div className="w-find-none">No minor findings in this revision.</div>}
      <div className="w-find-foot">{sorts.find(row => row[0] === sort)[2]}{onlyMinor ? ' · other verdicts hidden' : ''}</div>
      {!!qualification.length && (
        <div style={{ marginTop: 16 }} data-sabre-qual-findings="1">
          <div className="w-sec-h">Qualification findings<span className="w-grow"></span><span>{qualification.length}</span></div>
          {qualification.map((finding, index) => {
            const detail = finding && finding.detail;
            const label = finding && finding.label || 'Qualification finding';
            return <div className="w-find" key={label + index}><span className="dot" style={{ width: 9, height: 9, borderRadius: 5, marginTop: 5, flexShrink: 0, background: 'var(--fail)' }}></span><span style={{ flex: 1, minWidth: 0 }}><span className="w-find-n" style={{ display: 'block' }}>{label}</span><span className="w-find-d" style={{ display: 'block' }}>{detail || 'No finding detail was materialized.'}</span></span></div>;
          })}
        </div>
      )}
      {!!loose.length && (
        <div style={{ marginTop: 16 }}>
          <div className="w-sec-h">Unplaced findings<span className="w-grow"></span><span>{loose.length}</span></div>
          {loose.map((finding, index) => <div className="w-find" key={(finding.label || finding.section || 'finding') + index}><span className="dot" style={{ width: 9, height: 9, borderRadius: 5, marginTop: 5, flexShrink: 0, background: finding.tag === 'major' ? 'var(--fail)' : finding.tag === 'pass' ? 'var(--pass)' : 'var(--cond)' }}></span><span style={{ flex: 1, minWidth: 0 }}><span className="w-find-n" style={{ display: 'block' }}>{finding.label || finding.section || 'Build-level finding'}</span><span className="w-find-d" style={{ display: 'block' }}>{finding.note || 'No finding detail was materialized.'}</span></span></div>)}
        </div>
      )}
      {chase && !chase.wired && <div className="w-callout info" style={{ marginTop: 14 }}><BI.jet size={15} /><span>{chase.reply || SQ_CHASE_GATE}</span></div>}
    </div>
  );
}

function sqLegacySummary(markdown) {
  if (!markdown) return null;
  const lines = markdown.split('\n');
  let heading = null;
  let summary = null;
  for (let index = 0; index < lines.length && (!heading || !summary); index++) {
    const line = lines[index].trim();
    if (!heading && /^##\s/.test(line)) heading = line.replace(/^##\s+/, '').replace(/^[^A-Za-z]+\s*/, '');
    if (!summary && /^[^#>\s-]/.test(line) && line.length > 20 && line.length < 400) summary = line;
  }
  return { heading: heading || 'QA Report', summary: summary || markdown.slice(0, 280) };
}

function sqAfterburnerCard(current) {   // RW-04 (🐓): delegate to the contract card (phase vocabulary + verbatim eoa.reason) — was a re-implementation that rendered raw phase ids
  return window.reviewAfterburnerCard ? window.reviewAfterburnerCard(current) : { value: current && current.status ? String(current.status) : 'Not run', tone: null, status: current && current.status || 'not_started' };
}

function SqAfterburnerLane({ isLive, current }) {
  const card = isLive && current === undefined
    ? { value: 'Checking Afterburner status…', tone: null, status: 'loading' }
    : isLive && !current
      ? { value: 'Not materialized for this lane', tone: null, status: 'not_materialized' }
      : sqAfterburnerCard(current || { status: 'not_started' });
  return (
    <div className="w-sq-foot2" style={{ marginTop: 16 }} data-sabre-external-lane="afterburner">
      <span><b>Email on Acid</b> · external render-QA lane</span>
      <span className="w-grow" style={{ flex: 1 }}></span>
      {card.tone
        ? <span className={card.tone === 'neutral' ? 'chip' : 'pill pill-' + card.tone} data-afterburner-status={card.status}>{card.tone === 'pass' || card.tone === 'run' ? <span className="dot"></span> : card.tone === 'neutral' ? null : <BI.warn size={10} />}{card.value}</span>
        : <span className="muted" data-afterburner-status={card.status}>{card.value}</span>}
    </div>
  );
}

function SqLegacyReview({ job, isLive, B, afterburnerStatus, pre, setPre, notesOpen, notes, setNotes, unplaced, artifacts, revisionQa }) {
  const report = artifacts.report;
  const loading = artifacts.loading;
  const qaSummary = report && report.summary ? { summary: report.summary } : sqLegacySummary(artifacts.markdown);
  const imgRefAudit = artifacts.imgRefAudit;
  const textAccuracy = artifacts.textAccuracy;
  const actions = isLive ? [] : sqArray(pre);
  const done = actions.filter((action) => action.done).length;
  const verdict = (report && (report.verdict || report.status)) || job.verdict || (isLive ? 'UNMEASURED' : 'CONDITIONAL');
  const verdictCopy = isLive
    ? loading
      ? 'Checking the original build artifacts…'
      : (qaSummary && qaSummary.summary ? qaSummary.summary : verdict + ' · full report not materialized for this lane')
    : '“Build is production-grade. Two minor items want confirmation: rate-table legibility at 125% zoom, and the SBS disclaimer color (#343741 vs body #3B3C43). No broken references.”';
  let brokenRefs;
  let imageFallbacks;
  let textCard;
  if (!isLive) {
    brokenRefs = { value: 'None', tone: 'pass' };
    imageFallbacks = { value: 'None', tone: 'pass' };
    textCard = { value: 'PASS', tone: 'pass' };
  } else if (loading) {
    brokenRefs = { value: 'Checking…', tone: null };
    imageFallbacks = { value: 'Checking…', tone: null };
  } else if (imgRefAudit) {
    const missing = Array.isArray(imgRefAudit.missing) ? imgRefAudit.missing.length : null;
    const fallbackRows = imgRefAudit.forbidden_fallbacks ?? imgRefAudit.fallbacks_used;
    const fallbacks = Array.isArray(fallbackRows) ? fallbackRows.length : null;
    brokenRefs = { value: missing === null ? 'Unmeasured' : missing === 0 ? 'None' : missing + ' missing', tone: missing === null ? null : missing === 0 ? 'pass' : 'cond' };
    imageFallbacks = { value: fallbacks === null ? 'Unmeasured' : fallbacks === 0 ? 'None' : fallbacks + ' used', tone: fallbacks === null ? null : fallbacks === 0 ? 'pass' : 'cond' };
  } else {
    brokenRefs = { value: 'Not materialized for this lane', tone: null };
    imageFallbacks = { value: 'Not materialized for this lane', tone: null };
  }
  if (isLive) {
    if (loading) textCard = { value: 'Checking…', tone: null };
    else if (typeof textAccuracy?.accuracy_percent === 'number' && Number.isFinite(textAccuracy.accuracy_percent)) {
      textCard = { value: textAccuracy.accuracy_percent.toFixed(1) + '%', tone: 'neutral' };
    } else textCard = { value: 'Not materialized for this lane', tone: null };
  }
  const afterburner = isLive && afterburnerStatus === undefined
    ? { value: 'Checking Afterburner status…', tone: null, status: 'loading' }
    : isLive && !afterburnerStatus
      ? { value: 'Not materialized for this lane', tone: null, status: 'not_materialized' }
      : sqAfterburnerCard(afterburnerStatus || { status: 'not_started' });
  const renderValue = (card) => card.tone
    ? <span className={card.tone === 'neutral' ? 'chip' : 'pill pill-' + card.tone} style={{ fontSize: 11 }}>{card.tone === 'pass' || card.tone === 'run' ? <span className="dot"></span> : card.tone === 'neutral' ? null : <BI.warn size={10} />}{card.value}</span>
    : <span className="muted" style={{ fontSize: 11 }}>{card.value}</span>;
  return (
    <div className="w-scroll">
      <div className="w-stack">
        {isLive && window.RwRevisionQaCard && <window.RwRevisionQaCard evidence={revisionQa} />}
        <details className="w-original-evidence" open={!isLive} data-original-evidence="1"><summary>Original build report · historical, not current revision QA</summary>
        <div className={'verdict-banner ' + (verdict === 'PASS' ? 'pass' : verdict === 'FAIL' ? 'fail' : 'cond')} data-review-verdict={verdict}>
          <span className="vb-ico" style={{ color: verdict === 'PASS' ? 'var(--pass)' : verdict === 'FAIL' ? 'var(--fail)' : 'var(--cond)' }}><BI.warn size={22} /></span>
          <div className="grow"><div style={{ fontSize: 16, fontWeight: 800, color: verdict === 'PASS' ? 'var(--rw-pass-ink)' : verdict === 'FAIL' ? 'var(--rw-fail-ink)' : 'var(--rw-cond-ink)' }}>{verdict}</div><div className="muted" style={{ fontSize: 13, marginTop: 3 }}>{verdictCopy}</div></div>
        </div>
        <div data-review-qa={isLive ? 'live' : 'mock'}>
          <div className="w-sec-h">{isLive ? 'Original build measurements' : 'Demo QA'}<span className="w-grow"></span>{isLive && <span className="w-tag patch">historical</span>}{!!(B && B.touched && B.touched.size) && <span className="chip">Draft changes are not covered by these measurements</span>}</div>
          <div className="w-qa" style={{ flexShrink: 0 }}>
            <div className="w-qa-c" data-qa-card="Broken refs"><div className="w-qa-k">Broken refs</div><div className="w-qa-v">{renderValue(brokenRefs)}</div></div>
            <div className="w-qa-c" data-qa-card="Image fallbacks"><div className="w-qa-k">Image fallbacks</div><div className="w-qa-v">{renderValue(imageFallbacks)}</div></div>
            <div className="w-qa-c" data-qa-card="Text accuracy"><div className="w-qa-k">Text accuracy</div><div className="w-qa-v">{renderValue(textCard)}</div></div>
            <div className="w-qa-c" data-qa-card="Email on Acid">
              <div className="w-qa-k">Email on Acid</div>
              <div className="w-qa-v">
                {afterburner.tone
                  ? <span className={afterburner.tone === 'neutral' ? 'chip' : 'pill pill-' + afterburner.tone} style={{ fontSize: 11 }} data-afterburner-status={afterburner.status}>{afterburner.tone === 'pass' || afterburner.tone === 'run' ? <span className="dot"></span> : afterburner.tone === 'neutral' ? null : <BI.warn size={10} />}{afterburner.value}</span>
                  : <span className="muted" style={{ fontSize: 11 }} data-afterburner-status={afterburner.status}>{afterburner.value}</span>}
              </div>
            </div>
          </div>
        </div>
        </details>
        {!isLive && <div>
          <div className="w-sec-h">Pre-send actions<span className="w-grow"></span><span>{done}/{actions.length}</span></div>
          {actions.map((action, index) => <div key={action.id || index} className="check-item" onClick={() => typeof setPre === 'function' && setPre((items) => items.map((item, itemIndex) => (item.id || itemIndex) === (action.id || index) ? { ...item, done: !item.done } : item))} style={{ cursor: 'pointer' }}><span className={'check-box' + (action.done ? ' checked' : '')}>{action.done && <BI.check size={13} />}</span><span style={{ fontSize: 12.5, color: action.done ? 'var(--muted)' : 'var(--ink)', textDecoration: action.done ? 'line-through' : 'none' }}>{action.label}</span></div>)}
        </div>}
        {notesOpen && <div><label className="field-lbl">Change request notes</label><textarea className="input" rows={3} placeholder="e.g. increase rate-table font to 14px, confirm disclaimer #343741…" value={notes || ''} onChange={(event) => typeof setNotes === 'function' && setNotes(event.target.value)}></textarea></div>}
        <SqFindings B={B} unplaced={unplaced} />
      </div>
    </div>
  );
}

function RwSabreReviewTab(props) {
  const job = props.job || {};
  const engine = String(job.engine || (props.receipt && props.receipt.engine) || 'legacy').toLowerCase();
  const isSabre = engine === 'sabre';
  const sabre = sqUseSabreArtifacts(job, isSabre && !!props.isLive);
  const durableReview=sqUseQualificationReview(job,sabre.qual,isSabre&&!!props.isLive);
  const legacy = sqUseLegacyArtifacts(job, !isSabre && !!props.isLive);
  const [localVerdict, setVerdict] = React.useState(props.sqVerdict || null);
  const verdict=props.isLive?(durableReview.data?.active?{kind:durableReview.data.active.action==='approve'?'approve':'amend',note:'Saved exact-report verdict',event_digest:durableReview.data.last_event_digest}:null):localVerdict;
  const verdictScope = sabre.qual && (sabre.qual.report_digest || sabre.qual.report_id || sabre.qual.schema_version);
  const verdictRequired = !!(sabre.qual && sabre.qual.developer_verdict_required === true);
  const seatedScope = React.useRef(undefined);   // RW-02a integration (🐓): reset the verdict only when the qualification report actually changes
  React.useEffect(() => {
    if (props.isLive) return;
    window.dispatchEvent(new window.CustomEvent('rw-verdict-required', { detail: !!(verdictScope && verdictRequired) }));
    if (!verdictScope) return;
    if (!props.isLive && seatedScope.current === undefined && props.sqVerdict) {seatedScope.current = verdictScope;return;}
    if (seatedScope.current === verdictScope) return;
    seatedScope.current = verdictScope;
    setVerdict(null);
    if (verdictRequired) {
      window.dispatchEvent(new window.CustomEvent('rw-verdict', { detail: null }));
    }
  }, [job.id, verdictScope, verdictRequired,props.isLive]);
  const recordVerdict = (detail) => {
    setVerdict(detail);
    window.dispatchEvent(new window.CustomEvent('rw-verdict', { detail }));
  };

  if (!isSabre) return <SqLegacyReview {...props} artifacts={legacy} />;

  return (
    <div className="w-scroll w-pad w-review-qa">
      <div data-review-qa={props.isLive ? 'live' : 'mock'}>
        <SqReceiptStrip compact receipt={sabre.receipt} loading={sabre.loading} error={sabre.receiptError} qualified={!!sabre.qual} />
        <SqLegend />
        <SqStopCard receipt={sabre.receipt} evidence={sabre.stopEvidence}/>
        {props.isLive && window.RwRevisionQaCard && <window.RwRevisionQaCard evidence={props.revisionQa} review={props.currentReview}><SqCurrentRevisionReview review={props.currentReview} verdictOnly embedded /></window.RwRevisionQaCard>}
        <details className="w-original-evidence" open={!props.isLive} data-original-evidence="1" aria-label="Original build and qualification evidence">
        <summary>Original build and qualification evidence</summary>
        {props.isLive && <p className="w-bar-note" data-original-evidence-warning>Original build evidence · not saved-revision QA{!!props.B?.touched?.size && ' · local draft is not covered'}</p>}
        <SqLayerOneCard layer1={sabre.layer1} loading={sabre.loading} error={sabre.layer1Error} B={props.B} jobId={job.id} />
        {sabre.qualError && <div className="w-callout minor" data-sabre-qual-error="1"><BI.warn size={15} /><span><b>Qualification artifact could not be read</b> · {sabre.qualError}</span></div>}
        {sabre.qual && <SqQualificationCard report={sabre.qual} B={props.B} verdict={verdict} onVerdict={recordVerdict} review={props.isLive?durableReview:null} />}
        </details>
        <SqFindings B={props.B} chase={props.chase} unplaced={props.unplaced} qual={sabre.qual} />
        <SqRegions report={props.currentReview?.data?.report || sabre.qual} layer1={!props.currentReview?.data?.report ? sabre.layer1 : null} current={props.currentReview?.state === 'ready' && props.currentReview?.data?.current === true && !props.currentReview?.error} B={props.B}/>
        <SqPreSendAndNotes isLive={props.isLive} afterburnerStatus={props.afterburnerStatus} pre={props.pre} setPre={props.setPre} notesOpen={props.notesOpen} notes={props.notes} setNotes={props.setNotes} />
        <div className="w-ab-foot">Layer 1 runs on every SABRE build; layer 2 appears only for qualification runs. Email on Acid remains an external lane.</div>
      </div>
    </div>
  );
}

Object.assign(window, { RwSabreReviewTab, SQ_CHECKS, SqLedger, SqLegend, SqCurrentRevisionReview, SqLayerOneCard, SqStopCard, SqRegions, sqRegionGlyph, SqQualificationCard, SqFindings, SqPreSendAndNotes });
